import { createRouter, createWebHistory } from 'vue-router'
import { supabase } from './supabase'
import { getCurrentRole, setCurrentRole } from './composables/useOrgSettings'

// Routes a technician is allowed to visit. Anything else 403s for them.
const TECHNICIAN_ALLOWED = [
  /^\/operations(\/|$)/,
  /^\/login$/,
]

const routes = [
  { path: '/', redirect: '/dashboard' },
  { path: '/login', component: () => import('./pages/Login.vue'), meta: { public: true } },
  { path: '/dashboard', component: () => import('./pages/Dashboard.vue') },
  { path: '/templates', component: () => import('./pages/Templates.vue') },
  { path: '/templates/:id', component: () => import('./pages/TemplateDesigner.vue') },
  { path: '/reports', component: () => import('./pages/Reports.vue') },
  { path: '/rt-jobs/upload', component: () => import('./pages/RTJobUpload.vue') },
  { path: '/rt-jobs/:id', component: () => import('./pages/RTJobDetail.vue') },
  { path: '/rt-jobs', component: () => import('./pages/RTJobs.vue') },
  { path: '/timesheets/generate', component: () => import('./pages/TimesheetGenerator.vue') },
  { path: '/employees', component: () => import('./pages/Employees.vue') },
  { path: '/employees/:id', component: () => import('./pages/EmployeeDetail.vue') },
  { path: '/customers', component: () => import('./pages/Customers.vue') },
  { path: '/customers/:id', component: () => import('./pages/CustomerDetail.vue') },
  {
    path: '/invoices',
    component: () => import('./pages/Invoices.vue'),
    meta: { wide: true, fullHeight: true },
    children: [
      { path: ':id', component: () => import('./pages/InvoiceDetail.vue') }
    ]
  },
  { path: '/quotations', component: () => import('./pages/Quotations.vue') },
  { path: '/quotations/:id', component: () => import('./pages/QuotationDetail.vue') },
  { path: '/payments', component: () => import('./pages/Payments.vue') },
  { path: '/payments/:id', component: () => import('./pages/PaymentDetail.vue') },
  { path: '/credit-notes', component: () => import('./pages/CreditNotes.vue') },
  { path: '/credit-notes/new', component: () => import('./pages/CreditNoteDetail.vue') },
  { path: '/credit-notes/:id', component: () => import('./pages/CreditNoteDetail.vue') },
  { path: '/settings/org', component: () => import('./pages/OrgSettings.vue') },
  { path: '/users', component: () => import('./pages/Users.vue') },
  { path: '/issues', component: () => import('./pages/Issues.vue') },
  { path: '/audit', component: () => import('./pages/Audit.vue') },
  { path: '/approvals', component: () => import('./pages/Approvals.vue') },

  // NDT report submission — accessible to technicians + admins/owners
  { path: '/operations', redirect: '/operations/ndt' },
  { path: '/operations/ndt', component: () => import('./pages/NdtRequests.vue') },
  { path: '/operations/ndt/new', component: () => import('./pages/NdtRequestNew.vue') },
  { path: '/operations/ndt/:id', component: () => import('./pages/NdtReportEditor.vue') },

  { path: '/f/:id', component: () => import('./pages/PublicForm.vue'), meta: { public: true } }
]

export const router = createRouter({ history: createWebHistory(), routes })

async function loadRoleIfMissing(): Promise<string | null> {
  const cached = getCurrentRole()
  if (cached) return cached
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return null
  const userId = session.user.id
  const activeOrgId = (session.user as any)?.app_metadata?.active_org_id
  if (!activeOrgId) return null
  const { data } = await supabase.from('org_members')
    .select('role').eq('user_id', userId).eq('org_id', activeOrgId).maybeSingle()
  const role = (data as any)?.role || null
  setCurrentRole(role)
  return role
}

router.beforeEach(async (to) => {
  if (to.meta.public) return true
  const { data } = await supabase.auth.getSession()
  if (!data.session) return { path: '/login', query: { next: to.fullPath } }

  const role = await loadRoleIfMissing()
  if (role === 'technician') {
    const allowed = TECHNICIAN_ALLOWED.some(rx => rx.test(to.path))
    if (!allowed) return { path: '/operations/ndt' }
  }
  return true
})
